import type React from 'react'
import { useRef } from 'react'

interface DeferredCommitInputOptions {
  /** Validate and commit the typed text; returns true when the field may be released. */
  tryCommitInput: (reason: 'enter' | 'blur') => boolean
  /** Revert to the last committed value without applying the draft. */
  onEscape: () => void
  onFocus: () => void
  onDraftChange: (text: string) => void
}

/**
 * Handlers for a commit-on-leave numeric field: Enter commits (and releases focus on success),
 * blur commits one microtask later, and Esc reverts.
 *
 * Esc reverts to the last committed value and clears any error, without applying or committing
 * whatever is currently typed — the object stays selected. Blurring the field (so a subsequent
 * keypress isn't swallowed by this input) would otherwise re-trigger `onBlur`'s commit logic on
 * the very value we just reverted away from, so the next blur is suppressed via
 * `suppressNextBlurCommitRef`.
 */
export function useDeferredCommitInput({
  tryCommitInput,
  onEscape,
  onFocus,
  onDraftChange,
}: DeferredCommitInputOptions) {
  const suppressNextBlurCommitRef = useRef(false)

  return {
    onFocus,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onDraftChange(e.target.value)
    },
    onBlur: () => {
      if (suppressNextBlurCommitRef.current) {
        suppressNextBlurCommitRef.current = false
        return
      }

      // A canvas click that moves the selection blurs this field *explicitly*, from
      // inside the clicked shape's own pointerdown handler (shape-renderer/handlers/shape-pointer-handlers.ts's
      // `document.activeElement?.blur()`), synchronously BEFORE that same handler applies
      // the new selection (`onClick?.(shape, event)` runs right after). So at the instant
      // this handler fires, the live selection still looks unchanged and the guard in
      // `tryCommitInput` would miss it. Deferring one microtask lets that selection change
      // land first — it always happens later in the same synchronous call stack — so the
      // guard sees the new selection and discards the draft instead of committing it to
      // the shapes that used to be selected.
      queueMicrotask(() => {
        tryCommitInput('blur')
      })
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (tryCommitInput('enter')) {
          ;(e.target as HTMLInputElement).blur()
        }

        return
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        suppressNextBlurCommitRef.current = true
        ;(e.target as HTMLInputElement).blur()
      }
    },
  }
}
